import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { Release, Earnings, Statement, PayoutInfo, TaxForm, Artist, ArtistFormData, Notification, NotificationType, Ticket, TicketMessage, TicketFormData, TicketMessageFormData, TicketStatusUpdate, PayPalInfo } from '../types';
import { supabase } from '../lib/supabase';
import { useAuth } from './AuthContext';

// CloudFront CDN domain for admin panel file access
const CLOUDFRONT_DOMAIN = 'https://d1e4jwc7nuduiz.cloudfront.net';

interface CachedData {
  data: any;
  timestamp: number;
}

interface DataCache {
  releases: CachedData | null;
  earnings: CachedData | null;
  artists: CachedData | null;
  notifications: CachedData | null;
  tickets: CachedData | null;
  payoutInfo: CachedData | null;
  paypalInfo: CachedData | null;
}

interface DataContextType {
  releases: Release[];
  earnings: Earnings[];
  statements: Statement[];
  payoutInfo: PayoutInfo | null;
  taxForms: TaxForm[];
  artists: Artist[];
  notifications: Notification[];
  unreadNotificationCount: number;
  createRelease: (release: Omit<Release, 'id' | 'created_at'> & { cover_art_file?: File }) => Promise<boolean>;
  updateReleaseStatus: (releaseId: string, status: Release['status'], rejectReason?: string) => Promise<boolean>;
  withdrawApproval: (releaseId: string) => Promise<boolean>;
  submitForApproval: (releaseId: string) => Promise<boolean>;
  updateRelease: (releaseId: string, releaseData: Partial<Release> & { cover_art_file?: File }) => Promise<boolean>;
  deleteRelease: (releaseId: string) => Promise<boolean>;
  updatePayoutInfo: (info: PayoutInfo) => Promise<boolean>;
  getEarningsByMonth: (month: string) => Earnings[];
  getTotalEarnings: () => number;
  addManualEarning: (earningData: {
    artist_id: string;
    platforms: string[];
    track_ids: string[];
    amount: number;
    currency: string;
    month: string;
    payment_date: string;
    notes?: string;
  }) => Promise<boolean>;
  createArtist: (artistData: ArtistFormData) => Promise<boolean>;
  updateArtist: (artistId: string, artistData: Partial<Artist>) => Promise<boolean>;
  deleteArtist: (artistId: string) => Promise<boolean>;
  getArtistById: (artistId: string) => Artist | undefined;
  uploadCoverArt: (file: File, releaseId: string) => Promise<string | null>;
  uploadAudioFile: (file: File, trackId: string) => Promise<string | null>;
  getAudioFileUrl: (trackId: string) => string | null;
  getCoverArtUrl: (releaseId: string) => string | null;
  // CloudFront URL generators (admin panel direct access)
  getCloudFrontAudioUrl: (s3Key: string) => string;
  getCloudFrontCoverUrl: (s3Key: string) => string;
  // Hybrid storage functions for S3 + CloudFront + Supabase
  getHybridAudioFileUrl: (trackId: string) => Promise<string | null>;
  getHybridCoverArtUrl: (releaseId: string) => Promise<string | null>;
  getS3AudioFileUrl: (trackId: string) => Promise<string | null>;
  getS3CoverArtUrl: (releaseId: string) => Promise<string | null>;
  fetchReleases: () => Promise<void>;
  fetchEarnings: () => Promise<void>;
  fetchArtists: () => Promise<void>;
  // Notification functions
  fetchNotifications: () => Promise<void>;
  createNotification: (notification: Omit<Notification, 'id' | 'created_at'>) => Promise<boolean>;
  markNotificationAsRead: (notificationId: string) => Promise<boolean>;
  markAllNotificationsAsRead: () => Promise<boolean>;
  deleteNotification: (notificationId: string) => Promise<boolean>;
  sendNotificationToUser: (userId: string, type: NotificationType, title: string, message: string, actionUrl?: string, metadata?: any) => Promise<boolean>;
  sendNotificationToAdmins: (type: NotificationType, title: string, message: string, actionUrl?: string, metadata?: any) => Promise<boolean>;
  sendNotificationToArtist: (artistId: string, type: NotificationType, title: string, message: string, actionUrl?: string, metadata?: any) => Promise<boolean>;
  // Support Ticket functions
  tickets: Ticket[];
  ticketMessages: TicketMessage[];
  createTicket: (ticketData: TicketFormData) => Promise<boolean>;
  fetchTickets: () => Promise<void>;
  getTicketById: (ticketId: string) => Ticket | undefined;
  addTicketMessage: (messageData: TicketMessageFormData) => Promise<boolean>;
  updateTicketStatus: (ticketId: string, status: TicketStatusUpdate) => Promise<boolean>;
  fetchTicketMessages: (ticketId: string) => Promise<TicketMessage[]>;
  deleteTicket: (ticketId: string) => Promise<boolean>;
  // PayPal functions
  paypalInfo: PayPalInfo | null;
  savePayPalInfo: (paypalData: { full_name: string; paypal_email: string }) => Promise<boolean>;
  fetchPayPalInfo: () => Promise<void>;
  getPayPalInfoByUserId: (userId: string) => Promise<PayPalInfo | null>;
  isLoading: boolean;
}

const DataContext = createContext<DataContextType | undefined>(undefined);

export const useData = () => {
  const context = useContext(DataContext);
  if (context === undefined) {
    throw new Error('useData hook sadece DataProvider içinde kullanılabilir');
  }
  return context;
};

interface DataProviderProps {
  children: ReactNode;
}

export const DataProvider: React.FC<DataProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const [releases, setReleases] = useState<Release[]>([]);
  const [earnings, setEarnings] = useState<Earnings[]>([]);
  const [statements, setStatements] = useState<Statement[]>([]);
  const [payoutInfo, setPayoutInfo] = useState<PayoutInfo | null>(null);
  const [paypalInfo, setPaypalInfo] = useState<PayPalInfo | null>(null);
  const [taxForms, setTaxForms] = useState<TaxForm[]>([]);
  const [artists, setArtists] = useState<Artist[]>([]);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [ticketMessages] = useState<TicketMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [dataCache, setDataCache] = useState<DataCache>({
    releases: null,
    earnings: null,
    artists: null,
    notifications: null,
    tickets: null,
    payoutInfo: null,
    paypalInfo: null
  });

  // Cache durumu - 5 dakika (300000ms)
  const CACHE_DURATION = 5 * 60 * 1000;

  // Cache kontrol fonksiyonu
  const isCacheValid = (cachedData: CachedData | null): boolean => {
    if (!cachedData) return false;
    return Date.now() - cachedData.timestamp < CACHE_DURATION;
  };

  // Cache'den veri al veya API'den çek
  const getCachedOrFetch = async <T,>(
    cacheKey: keyof DataCache,
    fetchFunction: () => Promise<T>,
    setter: (data: T) => void
  ): Promise<void> => {
    const cached = dataCache[cacheKey];
    
    if (isCacheValid(cached) && cached) {
      setter(cached.data);
      return;
    }

    try {
      const data = await fetchFunction();
      setter(data);
      
      // Cache'i güncelle
      setDataCache(prev => ({
        ...prev,
        [cacheKey]: {
          data,
          timestamp: Date.now()
        }
      }));
    } catch (error) {
      console.error(`${cacheKey} veri çekme hatası:`, error);
    }
  };

  // Unread notification count hesaplama
  const unreadNotificationCount = notifications.filter(n => !n.read).length;

  // Raw fetch functions (cache'siz)
  const fetchReleasesData = async (): Promise<Release[]> => {
    if (!user) return [];

    // Auth'tan user ID'yi al
    const { data: { user: authUser } } = await supabase.auth.getUser();
    if (!authUser) return [];

    // Artist ise sadece kendi release'lerini getir (1000'den az olacağı için tek sorgu yeterli)
    if (user.role === 'artist') {
      const { data, error } = await supabase
        .from('releases')
        .select(`
          *,
          tracks (*),
          rights (*),
          contributors (*),
          split_pay (*),
          artists!releases_artist_id_fkey (
            id,
            display_name,
            first_name,
            last_name,
            email
          )
        `)
        .eq('artist_id', authUser.id);

      if (error) {
        console.error('Releases yükleme hatası:', error);
        return [];
      }
      return data || [];
    }

    // Admin için sayfalama ile TÜM release'leri çek (Supabase 1000 satır limitini aş)
    const PAGE_SIZE = 1000;
    let allReleases: Release[] = [];
    let from = 0;
    let hasMore = true;

    while (hasMore) {
      const { data, error } = await supabase
        .from('releases')
        .select(`
          *,
          tracks (*),
          rights (*),
          contributors (*),
          split_pay (*),
          artists!releases_artist_id_fkey (
            id,
            display_name,
            first_name,
            last_name,
            email
          )
        `)
        .range(from, from + PAGE_SIZE - 1)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Releases yükleme hatası:', error);
        break;
      }

      if (data && data.length > 0) {
        allReleases = [...allReleases, ...data];
        from += PAGE_SIZE;
        // Eğer gelen veri PAGE_SIZE'dan az ise daha fazla veri yok
        hasMore = data.length === PAGE_SIZE;
      } else {
        hasMore = false;
      }
    }

    console.log(`✅ Toplam ${allReleases.length} release yüklendi`);
    return allReleases;
  };

  // Releases'ları fetch et (cache'li)
  const fetchReleases = useCallback(async (forceRefresh = false) => {
    if (!user) return;
    
    setIsLoading(true);
    try {
      if (forceRefresh) {
        // Force refresh: bypass cache completely
        const data = await fetchReleasesData();
        setReleases(data);
        
        // Update cache with fresh data
        setDataCache(prev => ({
          ...prev,
          releases: {
            data,
            timestamp: Date.now()
          }
        }));
      } else {
        await getCachedOrFetch('releases', fetchReleasesData, setReleases);
      }
    } finally {
      setIsLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const fetchEarningsData = async (): Promise<Earnings[]> => {
    if (!user) return [];
    
    // Auth'tan user ID'yi al
    const { data: { user: authUser } } = await supabase.auth.getUser();
    if (!authUser) return [];

    let query = supabase.from('earnings').select('*');

    // Eğer user artist ise, sadece kendi earnings'lerini getir
    // Admin kullanıcıları tüm gelirleri görebilir
    if (user.role === 'artist') {
      query = query.eq('user_id', authUser.id);
    }
    // Admin users can see all earnings - no additional filtering needed

    const { data, error } = await query;

    if (error) {
      console.error('Earnings yükleme hatası:', error);
      return [];
    }

    return data || [];
  };

  // Earnings'ları fetch et (cache'li)
  const fetchEarnings = useCallback(async () => {
    if (!user) return;
    
    setIsLoading(true);
    try {
      await getCachedOrFetch('earnings', fetchEarningsData, setEarnings);
    } finally {
      setIsLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const fetchArtistsData = async (): Promise<Artist[]> => {
    if (!user) return [];

    // Admin için sayfalama ile TÜM artistleri çek (Supabase 1000 satır limitini aş)
    const PAGE_SIZE = 1000;
    let allArtists: Artist[] = [];
    let from = 0;
    let hasMore = true;

    while (hasMore) {
      const { data, error } = await supabase
        .from('artists')
        .select('*')
        .range(from, from + PAGE_SIZE - 1)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Artists yükleme hatası:', error);
        break;
      }

      if (data && data.length > 0) {
        allArtists = [...allArtists, ...data];
        from += PAGE_SIZE;
        hasMore = data.length === PAGE_SIZE;
      } else {
        hasMore = false;
      }
    }

    console.log(`✅ Toplam ${allArtists.length} artist yüklendi`);
    return allArtists;
  };

  // Artists'ları fetch et (cache'li)
  const fetchArtists = useCallback(async () => {
    if (!user) return;
    
    setIsLoading(true);
    try {
      await getCachedOrFetch('artists', fetchArtistsData, setArtists);
    } finally {
      setIsLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const fetchPayoutInfoData = async (): Promise<PayoutInfo | null> => {
    if (!user) return null;
    
    try {
      // Use the dedicated PayPal info function for admin panel
      const { data, error } = await supabase.rpc('get_paypal_info_for_admin', {
        artist_id_param: user.id
      });

      if (error) {
        console.error('PayPal info RPC hatası:', error);
        
        // Fallback: directly query payout_info table instead of using RPC
        const { data: fallbackData, error: fallbackError } = await supabase
          .from('payout_info')
          .select('*')
          .eq('user_id', user.id)
          .single();

        if (fallbackError) {
          console.warn('No payout info found for user:', user.id);
          return null;
        }
        
        if (fallbackData) {
          return {
            user_id: fallbackData.user_id,
            type: fallbackData.type as 'paypal' | 'bank',
            paypal_email: fallbackData.paypal_email,
            full_name: fallbackData.full_name,
            address: fallbackData.address || '',
            city: fallbackData.city || '',
            postal_code: fallbackData.postal_code || '',
            country: fallbackData.country || 'US',
            currency: fallbackData.currency as 'TRY' | 'USD' | 'EUR',
            iban: fallbackData.iban
          };
        }
        return null;
      }

      // Convert PayPal data to PayoutInfo format
      if (data && data.length > 0) {
        const paypalData = data[0];
        return {
          user_id: paypalData.user_id,
          type: 'paypal' as const,
          paypal_email: paypalData.paypal_email,
          full_name: paypalData.full_name,
          address: '', // PayPal doesn't require address
          city: '',
          postal_code: '',
          country: 'US', // Default for PayPal
          currency: 'USD' as const,
          iban: undefined
        };
      } else {
        return null;
      }
    } catch (error) {
      // Hata durumunda sessizce başarısız ol
      console.warn('Payout info fetch exception:', error);
      return null;
    }
  };

  // Payout info'yu fetch et (cache'li)
  const fetchPayoutInfo = useCallback(async () => {
    if (!user) return;
    
    try {
      await getCachedOrFetch('payoutInfo', fetchPayoutInfoData, setPayoutInfo);
    } catch (error) {
      console.warn('Payout info fetch exception:', error);
      setPayoutInfo(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // User değiştiğinde verileri yükle (cache'i temizle)
  useEffect(() => {
    if (user) {
      // Cache'i temizle
      setDataCache({
        releases: null,
        earnings: null,
        artists: null,
        notifications: null,
        tickets: null,
        payoutInfo: null,
        paypalInfo: null
      });
      
      fetchReleases();
      fetchEarnings();
      fetchPayoutInfo();
      if (user.role === 'admin') {
        fetchArtists();
      }
    } else {
      setReleases([]);
      setEarnings([]);
      setStatements([]);
      setPayoutInfo(null);
      setPaypalInfo(null);
      setTaxForms([]);
      setArtists([]);
      setNotifications([]);
      // Cache'i temizle
      setDataCache({
        releases: null,
        earnings: null,
        artists: null,
        notifications: null,
        tickets: null,
        payoutInfo: null,
        paypalInfo: null
      });
    }
  }, [user, fetchReleases, fetchEarnings, fetchPayoutInfo, fetchArtists]);



  // S3'e dosya yükleme (Admin için)
  const uploadFileToS3 = async (file: File, fileType: 'audio' | 'cover', userUniqueId: string): Promise<{ s3Key: string } | null> => {
    try {
      // Admin için upload URL al
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        console.error('❌ User not authenticated');
        return null;
      }

      const token = (await supabase.auth.getSession()).data.session?.access_token;
      if (!token) {
        console.error('❌ No access token');
        return null;
      }

      // admin-generate-upload-url edge function'ını çağır
      const response = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/admin-generate-upload-url`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          file_type: fileType,
          file_name: file.name,
          content_type: file.type,
          user_unique_id: userUniqueId
        })
      });

      if (!response.ok) {
        const error = await response.text();
        console.error('❌ Failed to get upload URL:', error);
        return null;
      }

      const { upload_url, file_key, form_data } = await response.json();

      // S3'e dosya yükle
      const formData = new FormData();
      Object.keys(form_data).forEach(key => {
        formData.append(key, form_data[key]);
      });
      formData.append('file', file);

      const uploadResponse = await fetch(upload_url, {
        method: 'POST',
        body: formData
      });

      if (!uploadResponse.ok) {
        console.error('❌ S3 upload failed:', await uploadResponse.text());
        return null;
      }

      return { s3Key: file_key };

    } catch (error) {
      console.error('❌ S3 upload exception:', error);
      return null;
    }
  };

  // Cover art yükleme
  const uploadCoverArt = async (file: File, releaseId: string): Promise<string | null> => {
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${releaseId}/cover.${fileExt}`;
      
      const { error } = await supabase.storage
        .from('cover-arts')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: true
        });

      if (error) {
        console.error('❌ Cover art yükleme hatası:', error);
        
        // RLS policy hatası için özel mesaj
        if (error.message?.includes('row-level security policy') || error.message?.includes('Unauthorized')) {
          console.warn('⚠️ Storage RLS policy hatası - Cover art yüklenemedi, ancak devam ediliyor...');
          return null; // Hata olsa da process devam etsin
        }
        
        return null;
      }

      const { data: { publicUrl } } = supabase.storage
        .from('cover-arts')
        .getPublicUrl(fileName);

      return publicUrl;
    } catch (error) {
      console.error('❌ Cover art yükleme exception:', error);
      return null;
    }
  };

  // Audio file yükleme
  const uploadAudioFile = async (file: File, trackId: string): Promise<string | null> => {
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${trackId}/audio.${fileExt}`;
      
      const { error } = await supabase.storage
        .from('audio-files')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: true
        });

      if (error) {
        console.error('❌ Audio file yükleme hatası:', error);
        return null;
      }

      const { data: { publicUrl } } = supabase.storage
        .from('audio-files')
        .getPublicUrl(fileName);

      return publicUrl;
    } catch (error) {
      console.error('❌ Audio file yükleme exception:', error);
      return null;
    }
  };

  // Audio file URL'ini getir (hybrid storage support)
  const getAudioFileUrl = (trackId: string): string | null => {
    try {
      // First, find the track to determine storage type
      const track = releases
        .flatMap(release => release.tracks || [])
        .find(track => `${track.release_id}_track_${track.track_number}` === trackId);

      if (track && track.audio_storage_type === 's3' && track.s3_audio_key) {
        // For S3 stored files, return null to indicate async loading needed
        return null;
      }
      
      // For Supabase storage (legacy)
      const wavFileName = `${trackId}/audio.wav`;
      const { data: { publicUrl } } = supabase.storage
        .from('audio-files')
        .getPublicUrl(wavFileName);
      
      return publicUrl;
    } catch (error) {
      console.error('Audio file URL getirme hatası:', error);
      return null;
    }
  };

  // S3 Audio file URL'ini asenkron olarak getir (signed URL)
  const getS3AudioFileUrl = async (trackId: string): Promise<string | null> => {
    try {
      const response = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/get-s3-url?type=audio&id=${trackId}`, {
        headers: {
          'Authorization': `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        return data.signed_url;
      } else {
        console.error('S3 audio URL alınamadı:', await response.text());
        return null;
      }
    } catch (error) {
      console.error('S3 audio file URL getirme hatası:', error);
      return null;
    }
  };

  // Hybrid audio file URL - edge function ile güvenli erişim
  const getHybridAudioFileUrl = async (trackId: string): Promise<string | null> => {
    try {
      // Find track by ID (supports both UUID and legacy format)
      let track = null;

      // Try to find by UUID first (database ID)
      track = releases
        .flatMap(release => release.tracks || [])
        .find(t => t.id === trackId);

      // If not found, try legacy format: ${release_id}_track_${track_number}
      if (!track) {
        track = releases
          .flatMap(release => release.tracks || [])
          .find(t => `${t.release_id}_track_${t.track_number}` === trackId);
      }

      if (!track) {
        console.warn('⚠️ Track not found:', trackId);
        return null;
      }

      // Check storage type and return appropriate URL
      if (track.audio_storage_type === 's3' && track.s3_audio_key) {
        // S3 storage - use edge function
        const response = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/get-hybrid-urls`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({
            type: 'audio',
            id: trackId
          })
        });

        if (response.ok) {
          const data = await response.json();
          return data.hybrid_url;
        } else {
          console.error('❌ Failed to get S3 audio URL:', await response.text());
          return null;
        }
      } else if (track.audio_file && typeof track.audio_file === 'string') {
        // Supabase storage - return direct URL
        return track.audio_file;
      } else {
        console.warn('⚠️ No audio file found for track');
        return null;
      }
    } catch (error) {
      console.error('❌ Audio URL error:', error);
      return null;
    }
  };

  // Cover art URL'ini getir (legacy Supabase storage)
  const getCoverArtUrl = (releaseId: string): string | null => {
    try {
      // Önce release'i bul ve cover_art field'ını kontrol et
      const release = releases.find(r => r.id === releaseId);
      if (release && release.cover_art && release.cover_art.trim() !== '') {
        return release.cover_art;
      }
      
      // Eğer release'te cover_art yoksa, storage'dan URL oluştur
      // Önce PNG dosyasını dene (çünkü veri PNG formatında kaydedilmiş)
      const pngFileName = `${releaseId}/cover.png`;
      const { data: { publicUrl: pngUrl } } = supabase.storage
        .from('cover-arts')
        .getPublicUrl(pngFileName);
      
      if (pngUrl) {
        return pngUrl;
      }
      
      // Sonra JPG dosyasını dene
      const jpgFileName = `${releaseId}/cover.jpg`;
      const { data: { publicUrl: jpgUrl } } = supabase.storage
        .from('cover-arts')
        .getPublicUrl(jpgFileName);
      
      return jpgUrl;
    } catch (error) {
      console.error('Cover art URL getirme hatası:', error);
      return null;
    }
  };

  // S3 Cover art URL'ini asenkron olarak getir (signed URL)
  const getS3CoverArtUrl = async (releaseId: string): Promise<string | null> => {
    try {
      const response = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/get-s3-url?type=cover&id=${releaseId}`, {
        headers: {
          'Authorization': `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        return data.signed_url;
      } else {
        console.error('S3 cover URL alınamadı:', await response.text());
        return null;
      }
    } catch (error) {
      console.error('S3 cover art URL getirme hatası:', error);
      return null;
    }
  };

  // CloudFront URL generators (for admin panel direct access to S3 files)
  const getCloudFrontAudioUrl = (s3Key: string): string => {
    return `${CLOUDFRONT_DOMAIN}/${s3Key}`;
  };

  const getCloudFrontCoverUrl = (s3Key: string): string => {
    return `${CLOUDFRONT_DOMAIN}/${s3Key}`;
  };

  // Hybrid cover art URL - edge function ile güvenli erişim
  const getHybridCoverArtUrl = async (releaseId: string): Promise<string | null> => {
    try {
      // Find release by ID
      const release = releases.find(r => r.id === releaseId);

      if (!release) {
        console.warn('⚠️ Release not found:', releaseId);
        return null;
      }

      // Check storage type and return appropriate URL
      if (release.cover_storage_type === 's3' && release.s3_cover_key) {
        // S3 storage - use edge function
        const response = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/get-hybrid-urls`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({
            type: 'cover',
            id: releaseId
          })
        });

        if (response.ok) {
          const data = await response.json();
          return data.hybrid_url;
        } else {
          console.error('❌ Failed to get S3 cover URL:', await response.text());
          return null;
        }
      } else if (release.cover_art) {
        // Supabase storage - return direct URL
        return release.cover_art;
      } else {
        console.warn('⚠️ No cover art found for release');
        return null;
      }
    } catch (error) {
      console.error('❌ Cover URL error:', error);
      return null;
    }
  };

  // Release oluştur
  const createRelease = async (releaseData: Omit<Release, 'id' | 'created_at'> & { cover_art_file?: File }): Promise<boolean> => {
    if (!user) return false;
    
    setIsLoading(true);

    
    try {
      // Release'i insert et
      const { data: releaseResult, error: releaseError } = await supabase
        .from('releases')
        .insert([{
          artist_id: releaseData.artist_id,
          title: releaseData.title,
          type: releaseData.type,
          release_date: releaseData.release_date,
          genre: releaseData.genre,
          subgenre: releaseData.subgenre,
          language: releaseData.language,
          artist_display_name: releaseData.artist_display_name,
          status: releaseData.status || 'draft',
          cover_art: '', // Önce boş, sonra güncelleyeceğiz
          description: releaseData.description,
          concept_description: releaseData.concept_description,
        }])
        .select()
        .single();

      if (releaseError) {
        console.error('Release oluşturma hatası:', releaseError);
        return false;
      }

      let coverArtUrl = '';
      
      // Cover art'ı yükle (eğer varsa)
      if ((releaseData as any).cover_art_file instanceof File) {
        const uploadedCoverUrl = await uploadCoverArt((releaseData as any).cover_art_file, releaseResult.id);
        if (uploadedCoverUrl) {
          coverArtUrl = uploadedCoverUrl;
          
          // Release'i cover art URL'iyle güncelle
          await supabase
            .from('releases')
            .update({ cover_art: coverArtUrl })
            .eq('id', releaseResult.id);
        }
      }

      // Track'leri insert et
      if (releaseData.tracks && releaseData.tracks.length > 0) {
        const tracksToInsert = [];
        
        for (let i = 0; i < releaseData.tracks.length; i++) {
          const track = releaseData.tracks[i];
          let audioFileUrl = '';
          
          // Audio file'ı yükle (eğer varsa)
          if ((track as any).audio_file instanceof File) {
            const trackId = `${releaseResult.id}_track_${i + 1}`;
            const uploadedAudioUrl = await uploadAudioFile((track as any).audio_file, trackId);
            if (uploadedAudioUrl) {
              audioFileUrl = uploadedAudioUrl;
            }
          }
          
          tracksToInsert.push({
            release_id: releaseResult.id,
            title: track.title,
            duration: track.duration,
            order_number: track.order,
            track_number: track.track_number,
            is_explicit: track.is_explicit,
            language: track.language,
            has_lyrics: track.has_lyrics,
            lyrics: track.lyrics,
            audio_file: audioFileUrl,
            ai_generated: track.ai_generated,
          });
        }

        const { error: tracksError } = await supabase
          .from('tracks')
          .insert(tracksToInsert);

        if (tracksError) {
          console.error('Track oluşturma hatası:', tracksError);
        }
      }

      // Rights'ı insert et (eğer varsa)
      if (releaseData.rights) {
        const { error: rightsError } = await supabase
          .from('rights')
          .insert([{
            release_id: releaseResult.id,
            isrc: releaseData.rights.isrc,
            upc: releaseData.rights.upc,
            phonogram_owner: releaseData.rights.phonogram_owner,
            composition_owner: releaseData.rights.composition_owner,
          }]);

        if (rightsError) {
          console.error('Rights oluşturma hatası:', rightsError);
        }
      }

      // Contributors'ı insert et (eğer varsa)
      if (releaseData.contributors && releaseData.contributors.length > 0) {
        const contributorsToInsert = releaseData.contributors.map(contributor => ({
          release_id: releaseResult.id,
          name: contributor.name,
          role: contributor.role,
        }));

        const { error: contributorsError } = await supabase
          .from('contributors')
          .insert(contributorsToInsert);

        if (contributorsError) {
          console.error('Contributors oluşturma hatası:', contributorsError);
        }
      }

      // Split Pay'leri insert et (eğer varsa)
      if (releaseData.split_pay && releaseData.split_pay.length > 0) {
        const splitPayToInsert = releaseData.split_pay.map(split => ({
          release_id: releaseResult.id,
          collaborator_name: split.collaborator_name,
          collaborator_url: split.collaborator_url,
          email: split.email,
          percentage: split.percentage,
          role: split.role,
        }));

        const { error: splitPayError } = await supabase
          .from('split_pay')
          .insert(splitPayToInsert);

        if (splitPayError) {
          console.error('Split Pay oluşturma hatası:', splitPayError);
        }
      }

      await fetchReleases();
      return true;
    } catch (error) {
      console.error('Release oluşturma hatası:', error);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  // Release status güncelle
  const updateReleaseStatus = async (releaseId: string, status: Release['status'], rejectReason?: string): Promise<boolean> => {
    setIsLoading(true);
    try {
      // Önce release'i bul
      const { data: releaseData, error: releaseError } = await supabase
        .from('releases')
        .select(`
          *,
          artists!releases_artist_id_fkey (
            id,
            display_name,
            user_id
          )
        `)
        .eq('id', releaseId)
        .single();

      if (releaseError) {
        console.error('Release bulma hatası:', releaseError);
        return false;
      }

      // Status ve reject reason güncelle
      const updateData: any = { status };
      if (status === 'rejected' && rejectReason) {
        updateData.reject_reason = rejectReason;
      } else if (status !== 'rejected') {
        // Rejected değilse reject_reason'ı temizle
        updateData.reject_reason = null;
      }

      // Get current status before update for webhook comparison
      const { data: beforeUpdate } = await supabase
        .from('releases')
        .select('status')
        .eq('id', releaseId)
        .single();

      const { data: updateResult, error } = await supabase
        .from('releases')
        .update(updateData)
        .eq('id', releaseId)
        .select()
        .single();

      if (error) {
        console.error('Release status güncelleme hatası:', error);
        return false;
      }

      // Manually create webhook since database trigger doesn't fire from admin panel
      if (beforeUpdate?.status !== updateResult?.status && beforeUpdate && updateResult) {
        const { data: externalSub } = await supabase
          .from('external_submissions')
          .select('id, webhook_url, app_info')
          .eq('release_id', releaseId)
          .single();

        if (externalSub?.webhook_url) {
          const webhookPayload = {
            release: {
              id: releaseId,
              title: updateResult.title,
              old_status: beforeUpdate.status,
              new_status: updateResult.status,
              artist_name: updateResult.artist_display_name,
              release_date: updateResult.release_date,
              type: updateResult.type,
              genre: updateResult.genre,
              subgenre: updateResult.subgenre,
              app_info: externalSub.app_info || updateResult.app_info,
              ...(updateResult.status === 'rejected' && updateResult.reject_reason && {
                reject_reason: updateResult.reject_reason
              })
            },
            event: 'release.status.changed',
            timestamp: new Date().toISOString(),
            webhook_version: '1.0',
            app_info: externalSub.app_info || updateResult.app_info
          };

          await supabase
            .from('webhook_queue')
            .insert({
              external_submission_id: externalSub.id,
              release_id: releaseId,
              webhook_url: externalSub.webhook_url,
              event_type: 'status_changed',
              payload: webhookPayload,
              status: 'pending',
              attempt_count: 0,
              max_attempts: 3
            });
        }
      }

      // Status değişikliğine göre bildirim gönder
      if (releaseData.artists && releaseData.artists.user_id) {
        let title = '';
        let message = '';
        let notificationType: NotificationType = 'system_update';
        let actionUrl = `/tracks/view/${releaseId}`;

        switch (status) {
          case 'approved':
            notificationType = 'release_approved';
            title = 'Müziğiniz Onaylandı!';
            message = `"${releaseData.title}" adlı müziğiniz onaylandı ve yayınlanmaya hazır.`;
            break;
          case 'submitted':
            notificationType = 'release_approved';
            title = 'Müziğiniz Platforma İletildi!';
            message = `"${releaseData.title}" adlı müziğiniz platforma iletildi ve yayınlanmayı bekliyor.`;
            break;
          case 'rejected':
            notificationType = 'release_rejected';
            title = 'Müziğiniz Reddedildi';
            message = rejectReason
              ? `"${releaseData.title}" adlı müziğiniz reddedildi. Sebep: ${rejectReason}`
              : `"${releaseData.title}" adlı müziğiniz reddedildi. Lütfen detayları kontrol ediniz.`;
            break;
          case 'published':
            notificationType = 'release_published';
            title = 'Müziğiniz Yayınlandı!';
            message = `"${releaseData.title}" adlı müziğiniz tüm platformlarda yayınlandı!`;
            break;
        }

        if (title && message) {
          // Fix: artists is an object, not array, so access user_id directly
          const artistUserId = releaseData.artists?.user_id;


          if (artistUserId) {
            try {
              const success = await sendNotificationToUser(
                artistUserId,
                notificationType,
                title,
                message,
                actionUrl,
                {
                  release_id: releaseId,
                  artist_id: releaseData.artist_id,
                  release_title: releaseData.title,
                  reject_reason: rejectReason
                }
              );

              if (!success) {
                console.error('Failed to send notification to user:', artistUserId);
              }
            } catch (error) {
              console.error('Notification sending error:', error);
            }
          } else {
            console.warn('Artist user_id not found for release:', releaseId, releaseData.artists);
          }
        }
      }

      // Force refresh to immediately show updated status
      setDataCache(prev => ({ ...prev, releases: null }));
      await fetchReleases(true);
      return true;
    } catch (error) {
      console.error('Release status güncelleme hatası:', error);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  // Release güncelle
  const updateRelease = async (releaseId: string, releaseData: Partial<Release> & { cover_art_file?: File }): Promise<boolean> => {
    setIsLoading(true);
    try {
      // Release'in artist bilgisini al (user_unique_id için)
      const { data: releaseWithArtist, error: fetchError } = await supabase
        .from('releases')
        .select('artist_id, artists!inner(user_unique_id)')
        .eq('id', releaseId)
        .single();

      if (fetchError || !releaseWithArtist) {
        console.error('❌ Release artist bilgisi alınamadı:', fetchError);
        return false;
      }

      const userUniqueId = (releaseWithArtist as any).artists.user_unique_id;
      if (!userUniqueId) {
        console.error('❌ Artist user_unique_id bulunamadı');
        return false;
      }

      // Cover art'ı yükle (S3'e - sadece yeni dosya varsa)
      let shouldUpdateCoverArt = false;
      let newS3CoverKey = '';

      if ((releaseData as any).cover_art_file instanceof File) {
        const uploadResult = await uploadFileToS3((releaseData as any).cover_art_file, 'cover', userUniqueId);
        if (uploadResult) {
          newS3CoverKey = uploadResult.s3Key;
          shouldUpdateCoverArt = true;
          console.log('✅ Cover art S3\'e yüklendi:', newS3CoverKey);
        } else {
          console.warn('⚠️ Cover art yüklenemedi, mevcut cover art korunacak');
        }
      }

      // Release'i güncelle (sadece geçerli kolonları gönder)
      const { cover_art_file, tracks, rights, contributors, split_pay, partners, ...cleanReleaseData } = releaseData as any;

      // Sadece releases tablosunda olan kolonları gönder
      const validReleaseUpdate: any = {
        title: cleanReleaseData.title,
        type: cleanReleaseData.type,
        release_date: cleanReleaseData.release_date,
        genre: cleanReleaseData.genre,
        subgenre: cleanReleaseData.subgenre,
        language: cleanReleaseData.language,
        artist_display_name: cleanReleaseData.artist_display_name,
        status: cleanReleaseData.status,
        description: cleanReleaseData.description,
        concept_description: cleanReleaseData.concept_description
      };

      // Cover art'ı sadece yeni dosya yüklendiğinde güncelle
      if (shouldUpdateCoverArt) {
        validReleaseUpdate.s3_cover_key = newS3CoverKey; // S3 key'i güncelle
        validReleaseUpdate.cover_storage_type = 's3'; // S3 storage kullanıyor
        validReleaseUpdate.cover_art = null; // Supabase URL'i temizle
      }
      
      // Undefined değerleri temizle
      Object.keys(validReleaseUpdate).forEach(key => {
        if ((validReleaseUpdate as any)[key] === undefined) {
          delete (validReleaseUpdate as any)[key];
        }
      });
      
      const { error: releaseError } = await supabase
        .from('releases')
        .update(validReleaseUpdate)
        .eq('id', releaseId);

      if (releaseError) {
        console.error('Release güncelleme hatası:', releaseError);
        return false;
      }

      // Track'leri güncelle (eğer varsa)
      if (tracks && tracks.length > 0) {
        for (let i = 0; i < tracks.length; i++) {
          const track = tracks[i];
          let shouldUpdateAudioFile = false;
          let newS3AudioKey = '';

          // Audio file'ı yükle (S3'e - sadece yeni dosya varsa)
          if ((track as any).audio_file instanceof File) {
            const uploadResult = await uploadFileToS3((track as any).audio_file, 'audio', userUniqueId);
            if (uploadResult) {
              newS3AudioKey = uploadResult.s3Key;
              shouldUpdateAudioFile = true;
              console.log('✅ Audio file S3\'e yüklendi:', newS3AudioKey);
            } else {
              console.warn('⚠️ Audio file yüklenemedi, mevcut audio file korunacak');
            }
          }

          // Track'i güncelle veya insert et
          const { audio_file, ...cleanTrackData } = track as any;

          if (track.id && track.id.indexOf('temp-') !== 0) {
            // Mevcut track'i güncelle

            const updateData: any = { ...cleanTrackData };

            // Audio file'ı sadece yeni dosya yüklendiğinde güncelle
            if (shouldUpdateAudioFile) {
              updateData.s3_audio_key = newS3AudioKey; // S3 key'i güncelle
              updateData.audio_storage_type = 's3'; // S3 storage kullanıyor
              updateData.audio_file = null; // Supabase URL'i temizle
            }
            
            const { error: updateError } = await supabase
              .from('tracks')
              .update(updateData)
              .eq('id', track.id);
            
            if (updateError) {
              console.error('❌ Track güncelleme hatası:', updateError);
            }
          } else {
            // Yeni track insert et
            const insertData = {
              ...cleanTrackData,
              release_id: releaseId,
              s3_audio_key: shouldUpdateAudioFile ? newS3AudioKey : null,
              audio_storage_type: shouldUpdateAudioFile ? 's3' : null,
              audio_file: null
            };
            
            const { error: insertError } = await supabase
              .from('tracks')
              .insert([insertData]);
            
            if (insertError) {
              console.error('❌ Track ekleme hatası:', insertError);
            }
          }
        }
      }

      // Rights'ı güncelle (eğer varsa)
      if (releaseData.rights) {
        // Önce mevcut rights kaydını sil
        await supabase
          .from('rights')
          .delete()
          .eq('release_id', releaseId);

        // Yeni rights kaydını ekle
        const { error: rightsError } = await supabase
          .from('rights')
          .insert([{
            release_id: releaseId,
            isrc: releaseData.rights.isrc,
            upc: releaseData.rights.upc,
            phonogram_owner: releaseData.rights.phonogram_owner,
            composition_owner: releaseData.rights.composition_owner,
          }]);

        if (rightsError) {
          console.error('Rights güncelleme hatası:', rightsError);
        }
      }

      // Contributors'ı güncelle (eğer varsa)
      if (releaseData.contributors) {
        // Önce mevcut contributors'ı sil
        await supabase
          .from('contributors')
          .delete()
          .eq('release_id', releaseId);

        // Yeni contributors'ı insert et
        if (releaseData.contributors.length > 0) {
          const contributorsToInsert = releaseData.contributors.map(contributor => ({
            release_id: releaseId,
            name: contributor.name,
            role: contributor.role,
          }));

          await supabase
            .from('contributors')
            .insert(contributorsToInsert);
        }
      }

      // Split Pay (Revenue Sharing)'i güncelle (eğer varsa)
      if (releaseData.split_pay) {
        // Önce mevcut split_pay'leri sil
        await supabase
          .from('split_pay')
          .delete()
          .eq('release_id', releaseId);

        // Yeni split_pay'leri insert et
        if (releaseData.split_pay.length > 0) {
          const splitPayToInsert = releaseData.split_pay.map(split => ({
            release_id: releaseId,
            collaborator_name: split.collaborator_name,
            collaborator_url: split.collaborator_url || '',
            email: split.email,
            percentage: split.percentage,
            role: split.role || 'collaborator',
          }));

          const { error: splitPayError } = await supabase
            .from('split_pay')
            .insert(splitPayToInsert);

          if (splitPayError) {
            console.error('Split pay güncelleme hatası:', splitPayError);
          }
        }
      }

      // Cache'i tamamen temizle ve force refresh yap
      setDataCache(prev => ({ ...prev, releases: null }));
      console.log('🧹 Release cache temizlendi, force refresh yapılıyor...');
      await fetchReleases(true); // Force refresh with cache bypass
      return true;
    } catch (error) {
      console.error('Release güncelleme hatası:', error);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  // Approval withdraw
  const withdrawApproval = async (releaseId: string): Promise<boolean> => {
    return await updateReleaseStatus(releaseId, 'draft');
  };

  // Submit for approval
  const submitForApproval = async (releaseId: string): Promise<boolean> => {
    setIsLoading(true);
    try {
      // Önce release'i bul
      const { data: releaseData, error: releaseError } = await supabase
        .from('releases')
        .select(`
          *,
          artists!releases_artist_id_fkey (
            id,
            display_name,
            user_id
          )
        `)
        .eq('id', releaseId)
        .single();

      if (releaseError) {
        console.error('Release bulma hatası:', releaseError);
        return false;
      }

      // Status'u pending yap
      const success = await updateReleaseStatus(releaseId, 'pending');
      
      if (success) {
        // Admin'lere bildirim gönder
        await sendNotificationToAdmins(
          'new_release_pending',
          'Yeni Müzik Onay Bekliyor',
          `"${releaseData.title}" adlı müzik ${releaseData.artists?.display_name || 'bir sanatçı'} tarafından onaya gönderildi.`,
          `/dashboard/admin/pending`,
          {
            release_id: releaseId,
            artist_id: releaseData.artist_id,
            release_title: releaseData.title,
            artist_name: releaseData.artists?.display_name
          }
        );
      }

      return success;
    } catch (error) {
      console.error('Submit for approval hatası:', error);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  // Payout info güncelle
  const updatePayoutInfo = async (info: PayoutInfo): Promise<boolean> => {
    if (!user) return false;
    
    setIsLoading(true);
    try {
      const { error } = await supabase
        .from('payout_info')
        .upsert([{
          user_id: user.id,
          type: info.type,
          paypal_email: info.paypal_email,
          iban: info.iban,
          full_name: info.full_name,
          address: info.address,
          city: info.city,
          postal_code: info.postal_code,
          country: info.country,
          currency: info.currency,
        }]);

      if (error) {
        // Farklı hata türlerini handle et
        if (error.code === '42P01') {
          console.warn('Payout info tablosu mevcut değil');
          return false;
        }
        
        // 406 (Not Acceptable) ve diğer permission hatalarını handle et
        if (error.message?.includes('permission denied') || 
            error.message?.includes('not acceptable') || 
            error.message?.includes('policy')) {
          console.warn('Payout info güncelleme izni yok - RLS policy gerekli olabilir:', error);
          return false;
        }
        
        console.error('Payout info güncelleme hatası:', error);
        return false;
      }

      await fetchPayoutInfo();
      return true;
    } catch (error) {
      // Hata durumunda sessizce başarısız ol
      console.warn('Payout info güncelleme exception:', error);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  // Artist oluştur
  const createArtist = async (artistData: ArtistFormData): Promise<boolean> => {
    setIsLoading(true);
    try {
      // Önce user oluştur - güvenli şifre generate et
      const generateSecurePassword = () => {
        const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*';
        let password = '';
        for (let i = 0; i < 12; i++) {
          password += chars.charAt(Math.floor(Math.random() * chars.length));
        }
        return password;
      };

      const securePassword = generateSecurePassword();
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: artistData.email,
        password: securePassword,
      });

      if (authError) {
        console.error('Artist user oluşturma hatası:', authError);
        return false;
      }

      if (authData.user) {
        // Users tablosuna ekle
        const { error: userError } = await supabase
          .from('users')
          .insert([{
            id: authData.user.id,
            email: authData.user.email,
            role: 'artist',
          }]);

        if (userError) {
          console.error('User profili oluşturma hatası:', userError);
          return false;
        }

        // Artists tablosuna ekle
        const { error: artistError } = await supabase
          .from('artists')
          .insert([{
            user_id: authData.user.id,
            first_name: artistData.first_name,
            last_name: artistData.last_name,
            email: artistData.email,
            display_name: artistData.display_name,
            bio: artistData.bio,
            status: 'active',
          }]);

        if (artistError) {
          console.error('Artist profili oluşturma hatası:', artistError);
          return false;
        }
      }

      await fetchArtists();
      return true;
    } catch (error) {
      console.error('Artist oluşturma hatası:', error);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  // Artist güncelle
  const updateArtist = async (artistId: string, artistData: Partial<Artist>): Promise<boolean> => {
    setIsLoading(true);
    try {
      const { error } = await supabase
        .from('artists')
        .update(artistData)
        .eq('id', artistId);

      if (error) {
        console.error('Artist güncelleme hatası:', error);
        return false;
      }

      await fetchArtists();
      return true;
    } catch (error) {
      console.error('Artist güncelleme hatası:', error);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  // Artist sil
  const deleteArtist = async (artistId: string): Promise<boolean> => {
    setIsLoading(true);
    try {
      const { error } = await supabase
        .from('artists')
        .update({ status: 'inactive' })
        .eq('id', artistId);

      if (error) {
        console.error('Artist silme hatası:', error);
        return false;
      }

      await fetchArtists();
      return true;
    } catch (error) {
      console.error('Artist silme hatası:', error);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  // Release sil
  const deleteRelease = async (releaseId: string): Promise<boolean> => {
    setIsLoading(true);
    try {
      // Önce ilgili tracks, rights ve contributor'ları sil
      const { error: tracksError } = await supabase
        .from('tracks')
        .delete()
        .eq('release_id', releaseId);

      if (tracksError) {
        console.error('Tracks silme hatası:', tracksError);
        return false;
      }

      const { error: rightsError } = await supabase
        .from('rights')
        .delete()
        .eq('release_id', releaseId);

      if (rightsError) {
        console.error('Rights silme hatası:', rightsError);
        return false;
      }

      const { error: contributorsError } = await supabase
        .from('contributors')
        .delete()
        .eq('release_id', releaseId);

      if (contributorsError) {
        console.error('Contributors silme hatası:', contributorsError);
        return false;
      }

      const { error: splitPayError } = await supabase
        .from('split_pay')
        .delete()
        .eq('release_id', releaseId);

      if (splitPayError) {
        console.error('Split pay silme hatası:', splitPayError);
        return false;
      }

      // Son olarak release'i sil
      const { error: releaseError } = await supabase
        .from('releases')
        .delete()
        .eq('id', releaseId);

      if (releaseError) {
        console.error('Release silme hatası:', releaseError);
        return false;
      }

      await fetchReleases();
      return true;
    } catch (error) {
      console.error('Release silme hatası:', error);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  // Helper functions
  const getEarningsByMonth = (month: string): Earnings[] => {
    return earnings.filter(earning => earning.month === month);
  };

  const getTotalEarnings = (): number => {
    return earnings.reduce((total, earning) => {
      return total + parseFloat(earning.revenue);
    }, 0);
  };

  const getArtistById = (artistId: string): Artist | undefined => {
    return artists.find(artist => artist.id === artistId);
  };

  // Add manual earning
  const addManualEarning = async (earningData: {
    artist_id: string;
    platforms: string[];
    track_ids: string[];
    amount: number;
    currency: string;
    month: string;
    payment_date: string;
    notes?: string;
  }): Promise<boolean> => {
    if (!user || user.role !== 'admin') return false;

    setIsLoading(true);
    try {
      const { data: { user: authUser } } = await supabase.auth.getUser();
      if (!authUser) return false;

      // Get artist info
      const selectedArtist = artists.find(a => a.id === earningData.artist_id);
      if (!selectedArtist) {
        console.error('Artist not found with ID:', earningData.artist_id);
        console.log('Available artists:', artists.map(a => ({ id: a.id, name: a.display_name })));
        return false;
      }

      console.log('Selected artist:', selectedArtist);

      // Get track names for display
      const trackNames = earningData.track_ids.length > 0 
        ? releases
            .filter(r => r.artist_id === earningData.artist_id)
            .flatMap(r => r.tracks.filter(t => earningData.track_ids.includes(t.id)))
            .map(t => t.title)
        : ['Manuel Eklenen Gelir'];

      const earningRecord = {
        user_id: selectedArtist.user_id,
        month: earningData.month,
        platform: earningData.platforms[0], // For backward compatibility
        platforms: earningData.platforms,
        track_name: trackNames[0] || 'Manuel Eklenen Gelir',
        track_ids: earningData.track_ids,
        streams: 0,
        revenue: earningData.amount,
        currency: earningData.currency,
        payment_status: 'pending',
        payment_date: null,
        is_manual_entry: true,
        created_by_admin: authUser.id,
        notes: earningData.notes || null
      };

      console.log('Inserting earning record:', earningRecord);

      const { data, error } = await supabase
        .from('earnings')
        .insert([earningRecord])
        .select();

      if (error) {
        console.error('Manuel gelir ekleme hatası:', error);
        console.error('Error details:', JSON.stringify(error, null, 2));
        return false;
      }

      console.log('Earning inserted successfully:', data);

      // Refresh earnings
      await fetchEarnings();

      return true;
    } catch (error) {
      console.error('Manuel gelir ekleme hatası:', error);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const fetchNotificationsData = async (): Promise<Notification[]> => {
    if (!user) return [];
    
    const { data: { user: authUser } } = await supabase.auth.getUser();
    if (!authUser) return [];

    const { data, error } = await supabase
      .from('notifications')
      .select('*')
      .eq('user_id', authUser.id)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Notifications yükleme hatası:', error);
      return [];
    }

    return data || [];
  };

  // Notification functions (cache'li)
  const fetchNotifications = useCallback(async () => {
    if (!user) return;
    
    setIsLoading(true);
    try {
      await getCachedOrFetch('notifications', fetchNotificationsData, setNotifications);
    } finally {
      setIsLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const createNotification = async (notification: Omit<Notification, 'id' | 'created_at'>): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from('notifications')
        .insert([notification]);

      if (error) {
        console.error('Notification oluşturma hatası:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Notification oluşturma hatası:', error);
      return false;
    }
  };

  const markNotificationAsRead = async (notificationId: string): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from('notifications')
        .update({ read: true })
        .eq('id', notificationId);

      if (error) {
        console.error('Notification okundu işaretleme hatası:', error);
        return false;
      }

      // Local state'i güncelle
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
      );

      return true;
    } catch (error) {
      console.error('Notification okundu işaretleme hatası:', error);
      return false;
    }
  };

  const markAllNotificationsAsRead = async (): Promise<boolean> => {
    if (!user) return false;

    try {
      const { data: { user: authUser } } = await supabase.auth.getUser();
      if (!authUser) return false;

      const { error } = await supabase
        .from('notifications')
        .update({ read: true })
        .eq('user_id', authUser.id)
        .eq('read', false);

      if (error) {
        console.error('Tüm notifications okundu işaretleme hatası:', error);
        return false;
      }

      // Local state'i güncelle
      setNotifications(prev => 
        prev.map(n => ({ ...n, read: true }))
      );

      return true;
    } catch (error) {
      console.error('Tüm notifications okundu işaretleme hatası:', error);
      return false;
    }
  };

  const deleteNotification = async (notificationId: string): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from('notifications')
        .delete()
        .eq('id', notificationId);

      if (error) {
        console.error('Notification silme hatası:', error);
        return false;
      }

      // Local state'i güncelle
      setNotifications(prev => prev.filter(n => n.id !== notificationId));

      return true;
    } catch (error) {
      console.error('Notification silme hatası:', error);
      return false;
    }
  };

  const sendNotificationToUser = async (
    userId: string, 
    type: NotificationType, 
    title: string, 
    message: string, 
    actionUrl?: string, 
    metadata?: any
  ): Promise<boolean> => {

    // Validate user exists before creating notification
    if (!userId) {
      console.warn('Cannot send notification: userId is empty');
      return false;
    }

    // Check if user exists using RPC function (bypasses RLS)
    const { data: userExists, error: userCheckError } = await supabase
      .rpc('validate_user_exists', { user_id_input: userId });


    if (userCheckError || !userExists) {
      console.warn('Cannot send notification: User not found:', userId, userCheckError);
      return false;
    }

    try {
      const result = await createNotification({
        user_id: userId,
        type,
        title,
        message,
        read: false,
        action_url: actionUrl,
        metadata
      });
      
      return result;
    } catch (error) {
      console.error('Notification creation failed:', error);
      return false;
    }
  };

  const sendNotificationToAdmins = async (
    type: NotificationType, 
    title: string, 
    message: string, 
    actionUrl?: string, 
    metadata?: any
  ): Promise<boolean> => {
    try {
      // Admin user'ları RPC ile bul (RLS bypass)
      const { data: adminUsers, error } = await supabase
        .rpc('get_admin_users');

      if (error) {
        console.error('Admin users bulma hatası:', error);
        return false;
      }

      if (!adminUsers || adminUsers.length === 0) {
        console.warn('Hiç admin kullanıcı bulunamadı!');
        return false;
      }

      // Her admin'e notification gönder
      const notificationPromises = adminUsers.map((admin: { id: string }) => 
        sendNotificationToUser(admin.id, type, title, message, actionUrl, metadata)
      );

      const results = await Promise.all(notificationPromises);
      return results.every(result => result === true);
    } catch (error) {
      console.error('Admin notifications gönderme hatası:', error);
      return false;
    }
  };

  const sendNotificationToArtist = async (
    artistId: string, 
    type: NotificationType, 
    title: string, 
    message: string, 
    actionUrl?: string, 
    metadata?: any
  ): Promise<boolean> => {
    try {
      // Artist'in user_id'sini bul
      const { data: artist, error } = await supabase
        .from('artists')
        .select('user_id')
        .eq('id', artistId)
        .single();

      if (error) {
        console.error('Artist bulma hatası:', error);
        return false;
      }

      return await sendNotificationToUser(artist.user_id, type, title, message, actionUrl, metadata);
    } catch (error) {
      console.error('Artist notification gönderme hatası:', error);
      return false;
    }
  };

  const fetchTicketsData = async (): Promise<Ticket[]> => {
    if (!user) return [];

    const { data: { user: authUser } } = await supabase.auth.getUser();
    if (!authUser) return [];

    let query = supabase
      .from('tickets')
      .select(`
        *,
        artists!tickets_artist_id_fkey (
          id,
          display_name,
          first_name,
          last_name,
          email
        ),
        releases!tickets_release_id_fkey (
          id,
          title
        )
      `)
      .order('created_at', { ascending: false });

    // Eğer user artist ise, sadece kendi biletlerini getir
    if (user.role === 'artist') {
      query = query.eq('user_id', authUser.id);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Tickets yükleme hatası:', error);
      return [];
    }

    // Her ticket için mesajları da yükle
    const ticketsWithMessages = await Promise.all(
      (data || []).map(async (ticket) => {
        const messages = await fetchTicketMessages(ticket.id);
        return {
          ...ticket,
          artist_name: ticket.artists?.display_name || 'Bilinmeyen Sanatçı',
          release_title: ticket.releases?.title || 'Bilinmeyen Müzik',
          messages: messages,
          unread_messages: messages.filter(m => !m.is_admin_response && user.role === 'admin').length
        };
      })
    );

    return ticketsWithMessages;
  };

  // Support Ticket Functions (cache'li)
  const fetchTickets = useCallback(async () => {
    if (!user) return;

    setIsLoading(true);
    try {
      await getCachedOrFetch('tickets', fetchTicketsData, setTickets);
    } finally {
      setIsLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const createTicket = async (ticketData: TicketFormData): Promise<boolean> => {
    if (!user) return false;

    try {
      const { data: { user: authUser } } = await supabase.auth.getUser();
      if (!authUser) return false;

             // Artist bilgisini al
       const { data: artist, error: artistError } = await supabase
         .from('artists')
         .select('id, display_name')
         .eq('user_id', authUser.id)
         .single();

      if (artistError || !artist) {
        console.error('Artist bilgisi alınamadı:', artistError);
        return false;
      }

      // Ticket oluştur
      const { data: newTicket, error: ticketError } = await supabase
        .from('tickets')
        .insert([{
          artist_id: artist.id,
          user_id: authUser.id,
          subject: ticketData.subject,
          description: ticketData.description,
          category: ticketData.category,
          priority: ticketData.priority,
          release_id: ticketData.release_id || null,
          status: 'open'
        }])
        .select()
        .single();

      if (ticketError) {
        console.error('Ticket oluşturma hatası:', ticketError);
        return false;
      }

      // İlk mesajı ekle
      const { error: messageError } = await supabase
        .from('ticket_messages')
        .insert([{
          ticket_id: newTicket.id,
          sender_id: authUser.id,
          content: ticketData.description,
          is_admin_response: false
        }]);

      if (messageError) {
        console.error('Ticket mesajı oluşturma hatası:', messageError);
        return false;
      }

      // Admin'lere bildirim gönder
      const releaseInfo = ticketData.release_id ? 
        releases.find(r => r.id === ticketData.release_id) : null;
      
      const notificationMessage = releaseInfo 
        ? `${ticketData.subject} - ${artist.display_name || 'Bilinmeyen Sanatçı'} (${releaseInfo.title})`
        : `${ticketData.subject} - ${artist.display_name || 'Bilinmeyen Sanatçı'}`;

      await sendNotificationToAdmins(
        'support_ticket_created',
        'Yeni Destek Bileti',
        notificationMessage,
        `/admin/support/${newTicket.id}`,
        { 
          ticket_id: newTicket.id, 
          artist_id: artist.id,
          release_id: ticketData.release_id || null
        }
      );

      // Tickets'ı yeniden yükle
      await fetchTickets();

      return true;
    } catch (error) {
      console.error('Ticket oluşturma hatası:', error);
      return false;
    }
  };

  const fetchTicketMessages = async (ticketId: string): Promise<TicketMessage[]> => {
    try {
      const { data, error } = await supabase
        .from('ticket_messages')
        .select(`
          *,
          users!ticket_messages_sender_id_fkey (
            id,
            email,
            role
          )
        `)
        .eq('ticket_id', ticketId)
        .order('created_at', { ascending: true });

      if (error) {
        console.error('Ticket mesajları yükleme hatası:', error);
        return [];
      }

      return data.map(message => ({
        ...message,
        sender: message.users
      })) || [];
    } catch (error) {
      console.error('Ticket mesajları yükleme hatası:', error);
      return [];
    }
  };

  const addTicketMessage = async (messageData: TicketMessageFormData): Promise<boolean> => {
    if (!user) return false;

    try {
      const { data: { user: authUser } } = await supabase.auth.getUser();
      if (!authUser) return false;

      // Mesajı ekle
      const { error: messageError } = await supabase
        .from('ticket_messages')
        .insert([{
          ticket_id: messageData.ticket_id,
          sender_id: authUser.id,
          content: messageData.content,
          is_admin_response: messageData.is_admin_response
        }]);

      if (messageError) {
        console.error('Ticket mesajı ekleme hatası:', messageError);
        return false;
      }

      // Ticket'ı güncelle (updated_at)
      const { error: ticketUpdateError } = await supabase
        .from('tickets')
        .update({ updated_at: new Date().toISOString() })
        .eq('id', messageData.ticket_id);

      if (ticketUpdateError) {
        console.error('Ticket güncelleme hatası:', ticketUpdateError);
      }

      // Ticket bilgisini al
      const { data: ticket, error: ticketError } = await supabase
        .from('tickets')
        .select('user_id, subject, artists!tickets_artist_id_fkey(display_name)')
        .eq('id', messageData.ticket_id)
        .single();

      if (ticketError) {
        console.error('Ticket bilgisi alınamadı:', ticketError);
      } else {
        // Bildirim gönder
        const targetUserId = user.role === 'admin' ? ticket.user_id : null;
        const isAdminResponse = user.role === 'admin';

        if (isAdminResponse && targetUserId) {
          // Admin yanıtı - sanatçıya bildirim gönder
          await sendNotificationToUser(
            targetUserId,
            'support_ticket_replied',
            'Destek Biletinize Yanıt',
            `"${ticket.subject}" konulu biletinize yanıt verildi`,
            `/support/${messageData.ticket_id}`,
            { ticket_id: messageData.ticket_id }
          );
        } else {
          // Sanatçı yanıtı - admin'lere bildirim gönder
          await sendNotificationToAdmins(
            'support_ticket_replied',
            'Destek Biletine Yanıt',
                         `"${ticket.subject}" - ${ticket.artists && ticket.artists.length > 0 ? ticket.artists[0].display_name : 'Bilinmeyen Sanatçı'}`,
            `/admin/support/${messageData.ticket_id}`,
            { ticket_id: messageData.ticket_id }
          );
        }
      }

      // Tickets'ı yeniden yükle
      await fetchTickets();

      return true;
    } catch (error) {
      console.error('Ticket mesajı ekleme hatası:', error);
      return false;
    }
  };

  const updateTicketStatus = async (ticketId: string, status: TicketStatusUpdate): Promise<boolean> => {
    if (!user || user.role !== 'admin') return false;

    try {
      const { data: { user: authUser } } = await supabase.auth.getUser();
      if (!authUser) return false;

      const updateData: any = {
        status: status,
        updated_at: new Date().toISOString()
      };

      if (status === 'resolved') {
        updateData.resolved_at = new Date().toISOString();
        updateData.resolved_by = authUser.id;
      }

      const { error } = await supabase
        .from('tickets')
        .update(updateData)
        .eq('id', ticketId);

      if (error) {
        console.error('Ticket durumu güncelleme hatası:', error);
        return false;
      }

      // Ticket bilgisini al
      const { data: ticket, error: ticketError } = await supabase
        .from('tickets')
        .select('user_id, subject, artists!tickets_artist_id_fkey(display_name)')
        .eq('id', ticketId)
        .single();

      if (!ticketError && ticket) {
        // Sanatçıya bildirim gönder
        await sendNotificationToUser(
          ticket.user_id,
          'support_ticket_status_changed',
          'Destek Bilet Durumu Değişti',
          `"${ticket.subject}" konulu biletinizin durumu değiştirildi: ${status}`,
          `/support/${ticketId}`,
          { ticket_id: ticketId, status: status }
        );
      }

      // Tickets'ı yeniden yükle
      await fetchTickets();

      return true;
    } catch (error) {
      console.error('Ticket durumu güncelleme hatası:', error);
      return false;
    }
  };

  const getTicketById = (ticketId: string): Ticket | undefined => {
    return tickets.find(ticket => ticket.id === ticketId);
  };

  const deleteTicket = async (ticketId: string): Promise<boolean> => {
    if (!user || user.role !== 'admin') return false;

    try {
      const { error } = await supabase
        .from('tickets')
        .delete()
        .eq('id', ticketId);

      if (error) {
        console.error('Ticket silme hatası:', error);
        return false;
      }

      // Local state'i güncelle
      setTickets(prev => prev.filter(ticket => ticket.id !== ticketId));

      return true;
    } catch (error) {
      console.error('Ticket silme hatası:', error);
      return false;
    }
  };

  // PayPal Functions
  const fetchPayPalInfo = useCallback(async () => {
    if (!user) return;

    try {
      const paypalInfo = await getPayPalInfoByUserId(user.id);
      setPaypalInfo(paypalInfo);
    } catch (error) {
      console.error('PayPal bilgileri fetch hatası:', error);
      setPaypalInfo(null);
    }
  }, [user]);

  const savePayPalInfo = async (paypalData: { full_name: string; paypal_email: string }): Promise<boolean> => {
    if (!user) {
      console.error('Kullanıcı bulunamadı');
      return false;
    }

    try {
      const { data, error } = await supabase
        .from('paypal_info')
        .upsert({
          user_id: user.id,
          full_name: paypalData.full_name,
          paypal_email: paypalData.paypal_email,
          updated_at: new Date().toISOString()
        })
        .select()
        .single();

      if (error) {
        console.error('PayPal bilgileri kaydetme hatası:', error);
        return false;
      }

      setPaypalInfo(data);
      return true;
    } catch (error) {
      console.error('PayPal bilgileri kaydetme hatası:', error);
      return false;
    }
  };

  const getPayPalInfoByUserId = async (userId: string): Promise<PayPalInfo | null> => {
    try {
      console.log(`🔍 Getting PayPal info for user: ${userId}`);
      
      const response = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/get-paypal-info`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          user_id: userId
        })
      });

      if (response.ok) {
        const data = await response.json();
        console.log(`✅ PayPal info retrieved successfully`);
        return data.paypal_info;
      } else {
        console.error('❌ Failed to get PayPal info:', await response.text());
        return null;
      }
    } catch (error) {
      console.error('❌ PayPal info fetch error:', error);
      return null;
    }
  };

  // Notifications ve tickets'ı user değiştiğinde yükle
  useEffect(() => {
    if (user) {
      fetchNotifications();
      fetchTickets();
      fetchPayPalInfo();
    }
  }, [user, fetchNotifications, fetchTickets, fetchPayPalInfo]);

  const value: DataContextType = {
    releases,
    earnings,
    statements,
    payoutInfo,
    taxForms,
    artists,
    notifications,
    unreadNotificationCount,
    createRelease,
    updateReleaseStatus,
    withdrawApproval,
    submitForApproval,
    updateRelease,
    deleteRelease,
    updatePayoutInfo,
    getEarningsByMonth,
    getTotalEarnings,
    addManualEarning,
    createArtist,
    updateArtist,
    deleteArtist,
    getArtistById,
    uploadCoverArt,
    uploadAudioFile,
    getAudioFileUrl,
    getCoverArtUrl,
    // CloudFront URL generators
    getCloudFrontAudioUrl,
    getCloudFrontCoverUrl,
    // Hybrid storage functions
    getHybridAudioFileUrl,
    getHybridCoverArtUrl,
    getS3AudioFileUrl,
    getS3CoverArtUrl,
    fetchReleases,
    fetchEarnings,
    fetchArtists,
    fetchNotifications,
    createNotification,
    markNotificationAsRead,
    markAllNotificationsAsRead,
    deleteNotification,
    sendNotificationToUser,
    sendNotificationToAdmins,
    sendNotificationToArtist,
    // Support Ticket functions
    tickets,
    ticketMessages,
    createTicket,
    fetchTickets,
    getTicketById,
    addTicketMessage,
    updateTicketStatus,
    fetchTicketMessages,
    deleteTicket,
    // PayPal functions
    paypalInfo,
    savePayPalInfo,
    fetchPayPalInfo,
    getPayPalInfoByUserId,
    isLoading
  };

  return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
}; 